import type { SVGProps } from 'react';

/**
 * Inline SVG icons (Lucide style, MIT). Inlining avoids a runtime dep
 * and stays under ~600 bytes per icon. All icons accept standard
 * SVGProps so callers can override size, color (via currentColor), etc.
 */

type IconProps = SVGProps<SVGSVGElement>;

const base = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

export function SendIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  );
}

export function StopIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function ShoppingCartIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
    </svg>
  );
}

export function ExternalLinkIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

/** Right-pointing chevron used for CTAs and list rows. */
export function ChevronRightIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/** Left-pointing chevron — carousel control. */
export function ChevronLeftIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/** Book icon — used to mark links pointing to /blog/ articles. */
export function BookIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

/** File/page icon — used to mark links pointing to static /pages/. */
export function FileIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

/** Thumbs up — feedback "helpful" button. */
export function ThumbsUpIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L15 2a2.55 2.55 0 0 1 0 3.88Z" />
    </svg>
  );
}

/** Thumbs down — feedback "not helpful" button. */
export function ThumbsDownIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <path d="M17 14V2" />
      <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H17a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L9 22a2.55 2.55 0 0 1 0-3.88Z" />
    </svg>
  );
}

/** X / close icon — used by dialogs. */
/** Up-right arrow — "opens another page". */
export function ArrowUpRightIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

export function XIcon(props: IconProps): JSX.Element {
  return (
    <svg {...base} aria-hidden="true" {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
